/**
 * AuditPackPreview - Preview of documents for a control audit pack
 *
 * Shows grouped documents that would be included in an audit pack,
 * with status indicators and a "generate" action.
 */

import React from 'react';
import { CheckCircle2, AlertCircle, Download, ChevronRight } from 'lucide-react';
import { ControlReadinessIndicator } from '../../atoms/ControlReadinessIndicator';
import type { MockDocumentType } from '../../types';
import './AuditPackPreview.css';

export type AuditPackDocumentStatus = 'present' | 'missing' | 'expired';

export interface AuditPackDocument {
  /** Unique document identifier */
  id: string;
  /** Document display name */
  name: string;
  /** Document type category */
  type: MockDocumentType;
  /** Document date (creation or issue date) */
  date?: Date | string;
  /** Source/origin of the document */
  source: string;
  /** Whether the document is present and valid */
  status: AuditPackDocumentStatus;
  /** Category for grouping */
  category: string;
}

export interface AuditPackGroup {
  /** Category name */
  name: string;
  /** Legal requirement for this category */
  legalBasis?: string;
  /** Documents in this category */
  documents: AuditPackDocument[];
}

export interface AuditPackPreviewProps {
  /** Control type name */
  controlType: string;

  /** Document groups */
  groups: AuditPackGroup[];

  /** Whether the audit pack is complete */
  isComplete: boolean;

  /** Callback when generate button is clicked */
  onGenerate?: () => void;

  /** Callback when a document is clicked */
  onDocumentClick?: (documentId: string) => void;

  /** Additional CSS class */
  className?: string;
}

export const AuditPackPreview: React.FC<AuditPackPreviewProps> = ({
  controlType,
  groups,
  isComplete,
  onGenerate,
  onDocumentClick,
  className = '',
}) => {
  const totalDocs = groups.reduce((sum, g) => sum + g.documents.length, 0);
  const presentDocs = groups.reduce(
    (sum, g) => sum + g.documents.filter((d) => d.status === 'present').length,
    0
  );

  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusIcon = (status: AuditPackDocument['status']) => {
    switch (status) {
      case 'present':
        return <CheckCircle2 className="audit-pack__status-icon audit-pack__status-icon--present" />;
      case 'missing':
        return <AlertCircle className="audit-pack__status-icon audit-pack__status-icon--missing" />;
      case 'expired':
        return <AlertCircle className="audit-pack__status-icon audit-pack__status-icon--expired" />;
    }
  };

  return (
    <div className={`audit-pack ${className}`}>
      {/* Header */}
      <header className="audit-pack__header">
        <div className="audit-pack__header-content">
          <h3 className="audit-pack__title">Prüfpaket: {controlType}</h3>
          <p className="audit-pack__subtitle">
            {presentDocs} von {totalDocs} Dokumenten vorhanden
          </p>
        </div>
        <ControlReadinessIndicator
          status={isComplete ? 'ready' : 'not-ready'}
          size="sm"
        />
      </header>

      {/* Document groups */}
      <div className="audit-pack__groups">
        {groups.map((group, groupIndex) => (
          <section key={groupIndex} className="audit-pack__group">
            <header className="audit-pack__group-header">
              <h4 className="audit-pack__group-name">{group.name}</h4>
              {group.legalBasis && (
                <span className="audit-pack__group-legal">{group.legalBasis}</span>
              )}
            </header>
            <ul className="audit-pack__document-list">
              {group.documents.map((doc) => (
                <li key={doc.id} className="audit-pack__document">
                  <button
                    type="button"
                    className={`audit-pack__document-button audit-pack__document-button--${doc.status}`}
                    onClick={() => onDocumentClick?.(doc.id)}
                    disabled={doc.status === 'missing'}
                  >
                    {getStatusIcon(doc.status)}
                    <span className="audit-pack__document-info">
                      <span className="audit-pack__document-name">{doc.name}</span>
                      {doc.status !== 'missing' && doc.date && (
                        <span className="audit-pack__document-date">{formatDate(doc.date)}</span>
                      )}
                      {doc.status === 'missing' && (
                        <span className="audit-pack__document-missing">Nicht vorhanden</span>
                      )}
                      {doc.status === 'expired' && (
                        <span className="audit-pack__document-expired">Abgelaufen</span>
                      )}
                    </span>
                    <ChevronRight className="audit-pack__document-arrow" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {/* Generate action */}
      <footer className="audit-pack__footer">
        <button
          type="button"
          className="audit-pack__generate-button"
          onClick={onGenerate}
          disabled={!isComplete}
        >
          <Download size={18} />
          <span>Prüfpaket erstellen</span>
        </button>
        {!isComplete && (
          <p className="audit-pack__incomplete-note">
            Prüfpaket kann erst erstellt werden, wenn alle Dokumente vorhanden sind.
          </p>
        )}
      </footer>
    </div>
  );
};

export default AuditPackPreview;
